import AiChatbot from '@/components/AiChatbot';
import WorkflowLogsModal from '@/components/modals/WorkflowLogsModal';
import OrgStateHandler from '@/components/OrgStateHandler';
import PostHogStateHandler from '@/components/PostHogStateHandler';
import UserStateHandler from '@/components/UserStateHandler';
import useLoadUser from '@/hooks/useLoadUser';
import PlatformRedirectHandler from '@/PlatformRedirectHandler';
import { useSignUp } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Toaster } from 'sonner';
import AiContextHandler from './components/AiContextHandler';
import AppDocDrawer from './components/AppDocDrawer';
import ArgumentOptionSlider from './components/arguments/ArgumentOptionSlider';
import CommandBar from './components/commandBar/CommandBar';
import ConnectivityStateHandler from './components/ConnectivityStateHandler';
import ContextMenu from './components/ContextMenu';
import DocDrawer from './components/DocDrawer';
import AppSnapshotsProvider from './components/drawers/AppSnapshotsProvider';
import WorkflowAutosavesProvider from './components/drawers/WorkflowAutosavesProvider';
import WorkflowSnapshotsProvider from './components/drawers/WorkflowSnapshotsProvider';
import SplashScreenHandler from './components/ErrorSplashHandler';
import EscapeKeyHandler from './components/EscapeKeyHandler';
import G2ReviewPrompt from './components/G2ReviewPrompt';
import LiveWorkflowLogsDrawer from './components/LiveWorkflowLogsDrawer';
import LoadingSplashScreen from './components/LoadingSplashScreen';
import CreateOrganizationModal from './components/modals/CreateOrganizationModal';
import ImageUploaderModal from './components/modals/ImageUploaderModal';
import IntegrationsOverviewModal from './components/modals/IntegrationsOverviewModal';
import MockDataTypesModal from './components/modals/MockDataTypesModal';
import PermissionsModal from './components/modals/PermissionsModal';
import TableModal from './components/modals/TableModal';
import UserProfileModal from './components/modals/UserProfileModal';
import PlatformAdminTwoFactorSplash from './components/platform/PlatformAdminTwoFactorSplash';
import PlatformHeader from './components/platform/PlatformHeader';
import PlatformIssueModal from './components/platform/PlatformIssueModal';
import PlatformLoader from './components/platform/PlatformLoader';
import PlatformMobileBanner from './components/platform/PlatformMobileBanner';
import PlatformOperationBanner from './components/platform/PlatformOperationBanner';
import PlatformRouter from './components/platform/PlatformRouter';
import ProjectAppsModal from './components/ProjectsTable/AppsCell/ProjectAppsModal';
import PubSubHandler from './components/PubSubHandler';
import AuditLogSlider from './components/sliders/AuditLogSlider';
import SyncHandler from './components/SyncHandler';
import TitleHandler from './components/TitleHandler';
import TooltipHandler from './components/TooltipHandler';
import PostSignUpHandler from './PostSignUpHandler';
import { useArgSelector } from './redux/curriedSelectors/curriedSelectors';
import selectors from './redux/selectors/selectors';
import SignUpLoader from './screens/auth/SignUpLoader';
import CredentialModal from './components/modals/CredentialModal.jsx';
import AppEditorIssueReportDrawer from './components/AppEditorIssueReportDrawer.jsx';
import AppEditorOutputDrawer from './components/AppEditorOutputDrawer.jsx';
import GravityWebsiteSignupHandoffHandler from './components/platform/handlers/GravityWebsiteSignupHandoffHandler.jsx';

const BLACKLISTED_PATHS = ['/oauth', '/signup', '/embedded-example'];

function Platform() {
  const { isLoaded, isLoggedIn, isEmbeddedEndUser } = useLoadUser();
  const { isLoaded: isSignUpLoaded } = useSignUp();
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const transitionSignUp = useSelector((state) => state.ui.transitionSignUp);

  const isNotApple = !/(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent) && !/(iPhone|iPod|iPad)/i.test(navigator.userAgent);
  const isBlacklisted = useMemo(() => BLACKLISTED_PATHS.some((path) => window.location.pathname.includes(path)), [window.location.pathname]);
  const isSignup = useMemo(() => window.location.pathname.includes('/signup'), [window.location.pathname]);
  const isAppsScreen = useMemo(() => window.location.pathname.includes('/apps'), [window.location.pathname]);

  return (
    <>
      <div className={`platform ${isNotApple ? 'not-apple' : ''} ${transitionSignUp ? 'signup' : ''} ${isSignup ? 'is-signup' : ''}`}>
        {(isLoaded || isLoggedIn === true) && (
          <>
            <PlatformMobileBanner />
            <PlatformOperationBanner />
            <PlatformHeader />
          </>
        )}
        <div className={'platform-body'}>
          <div className={'platform-screens'}>
            <SplashScreenHandler />
            {(isLoaded || isSignUpLoaded) && (
              <>
                <PlatformRouter isLoggedIn={isLoggedIn} isLoaded={isLoaded} isEmbeddedEndUser={isEmbeddedEndUser} />
                <Toaster richColors offset={{ right: 16, bottom: 16 }} />
                <G2ReviewPrompt />
                <DocDrawer /> {/* Manages doc drawer */}
                <WorkflowSnapshotsProvider /> {/* Manages workflow snapshots drawer */}
                <WorkflowAutosavesProvider /> {/* Manages workflow autosaves drawer */}
                <AppSnapshotsProvider /> {/* Manages app snapshots drawer */}
                <TitleHandler /> {/* Manages document title */}
                <LoadingSplashScreen />
                <PlatformLoader />
                <PlatformIssueModal />
                <CredentialModal />
                <PlatformAdminTwoFactorSplash />
              </>
            )}
          </div>
        </div>

        {/* {(isLoaded || isLoggedIn === true) && <PlatformFooter />} */}
        {isLoggedIn && isLoaded && (
          <>
            <CommandBar />
            <ArgumentOptionSlider appsScreen={isAppsScreen} />
            <LiveWorkflowLogsDrawer />
            <AppDocDrawer />
            <ImageUploaderModal />
            <CreateOrganizationModal />
            <UserProfileModal />
            <PermissionsModal />
            <WorkflowLogsModal />
            <MockDataTypesModal />
            <IntegrationsOverviewModal />
            <AuditLogSlider />
            <ProjectAppsModal />
            <TableModal />
            {isAppsScreen && <AppEditorIssueReportDrawer />}
            {isAppsScreen && <AppEditorOutputDrawer />}
            {!workflowId && !isBlacklisted && <AiChatbot />}
          </>
        )}
      </div>
      <SignUpLoader />
      <GravityWebsiteSignupHandoffHandler />
      <TooltipHandler /> {/* Manages global tooltips */}
      {isLoggedIn && (
        <>
          <UserStateHandler />
          <PostSignUpHandler />
          <PlatformRedirectHandler />
          {isLoaded && (
            <>
              {/* State */}
              <OrgStateHandler /> {/* Gets org-related state when it changes */}
              <ConnectivityStateHandler /> {/* Manages pub/sub related error state */}
              <PubSubHandler /> {/* Manages pub/sub setup & teardown */}
              <SyncHandler /> {/* Manages global sync state */}
              {/* Misc */}
              <EscapeKeyHandler /> {/* Handles global escape keypresses */}
              <ContextMenu /> {/* Manages global context menus */}
              <PostHogStateHandler />
              <AiContextHandler />
            </>
          )}
        </>
      )}
    </>
  );
}

export default Platform;
